import { FC, RefObject } from 'react';

import { BottomSheet, Button, Drop, VSpacing } from '@hh.ru/magritte-ui';
import { ArrowRightOutlinedSize24, ClockRewindOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { ReBalanceAlert } from 'components/common/BalanceCardContent/BalanceCardTop/AdditionalActions/ReBalanceAlert/ReBalanceAlert';
import { RefundModal } from 'components/common/BalanceCardContent/BalanceCardTop/AdditionalActions/RefundModal/RefundModal';

import styles from 'components/common/BalanceCardContent/BalanceCardTop/AdditionalActions/AdditionalActions.module.less';

const TrlKeys = {
    refund: 'Header.HeaderBalanceDrop.AdditionalActions.refund',
    reBalance: 'Header.HeaderBalanceDrop.AdditionalActions.reBalance',
};

interface AdditionalActionsProps {
    activatorRef: RefObject<HTMLButtonElement>;
    visible: boolean;
    onClose: () => void;
    isUserRefundEnabled: boolean;
    isReBalanceButtonEnabled: boolean;
}

export const AdditionalActions: FC<AdditionalActionsProps> = ({
    activatorRef,
    visible,
    onClose,
    isUserRefundEnabled,
    isReBalanceButtonEnabled,
}) => {
    const [isRefundModalVisible, setIsRefundModalVisibleOn, setIsRefundModalVisibleOff] = useOnOffState(false);
    const [isReBalanceAlertVisible, setIsReBalanceAlertVisibleOn, setIsReBalanceAlertVisibleOff] = useOnOffState(false);

    const trls = useTrls();

    const handleRefundModalClose = () => {
        setIsRefundModalVisibleOff();
        onClose();
    };

    const handleReBalanceAlertClose = () => {
        setIsReBalanceAlertVisibleOff();
        onClose();
    };

    if (!isUserRefundEnabled && !isReBalanceButtonEnabled) {
        return null;
    }

    const content = (
        <>
            {isUserRefundEnabled && (
                <Button
                    mode="secondary"
                    size="large"
                    style="neutral"
                    icon={<ArrowRightOutlinedSize24 />}
                    onClick={setIsRefundModalVisibleOn}
                    stretched
                    data-qa="header-balance-drop-refund-button"
                >
                    {trls(TrlKeys.refund)}
                </Button>
            )}
            {isReBalanceButtonEnabled && (
                <>
                    <VSpacing default={8} />
                    <Button
                        mode="secondary"
                        size="large"
                        style="neutral"
                        icon={<ClockRewindOutlinedSize24 />}
                        onClick={setIsReBalanceAlertVisibleOn}
                        stretched
                        data-qa="header-balance-drop-re-balance-button"
                    >
                        {trls(TrlKeys.reBalance)}
                    </Button>
                </>
            )}
        </>
    );

    return (
        <>
            <Drop
                activatorRef={activatorRef}
                visible={visible}
                onClose={onClose}
                placement="bottom-center"
                padding={12}
            >
                {content}
            </Drop>
            <BottomSheet visible={visible} onClose={onClose}>
                <div className={styles.bottomSheetWrapper}>{content}</div>
            </BottomSheet>
            <RefundModal visible={isRefundModalVisible} onClose={handleRefundModalClose} />
            <ReBalanceAlert visible={isReBalanceAlertVisible} onClose={handleReBalanceAlertClose} />
        </>
    );
};
